/**
 * Infinite Scroll Component for Shopify Collection Pages
 * 瀑布流自动加载组件 - 用于商品分类页面
 *
 * 使用方法：
 * 在 collection 模板中添加：
 * <infinite-scroll data-total-pages="{{ paginate.pages }}" data-current-page="{{ paginate.current_page }}">
 *   <div class="infinite-scroll__loading hidden">
 *     <div class="infinite-scroll__spinner"></div>
 *   </div>
 *   <div class="infinite-scroll__sentinel"></div>
 *   <div class="infinite-scroll__end hidden">已加载全部商品</div>
 * </infinite-scroll>
 */

const COLLECTION_RESTORE_KEY = 'luredolls_collection_restore_target';
const COLLECTION_RESTORE_SNAPSHOT_KEY = 'luredolls_collection_restore_snapshot';
const COLLECTION_RESTORE_SNAPSHOT_VERSION = 2;
const COLLECTION_RESTORE_MAX_AGE = 30 * 60 * 1000;
const COLLECTION_NATIVE_RESTORE_SETTLE_DELAY = 1800;

class InfiniteScroll extends HTMLElement {
  constructor() {
    super();
    this.currentPage = parseInt(this.dataset.currentPage) || 1;
    this.totalPages = parseInt(this.dataset.totalPages) || 1;
    this.loading = false;
    this.threshold = parseInt(this.dataset.threshold) || 200; // 距离分页组件多少像素时触发加载（默认200px）
    this.productGrid = null;
    this.loadingIndicator = null;
    this.sentinel = null;
    this.endMessage = null;
    this.observer = null;
    this.enabled = true;
    this.debug = false; // 生产环境关闭调试
    this.restoreHandler = null;
    this.restoreCleanup = null;
    this.restoreCreatedAt = 0;
    this.consumedRestoreCreatedAt = 0;
    this.restoreClearTimer = 0;
    this.restoringProductPosition = false;
  }

  log(...args) {
    if (this.debug) {
      console.log('[InfiniteScroll]', ...args);
    }
  }

  connectedCallback() {
    this.log('组件初始化...');

    // 查找商品网格容器
    this.productGrid = document.getElementById('product-grid');
    this.loadingIndicator = this.querySelector('.infinite-scroll__loading');
    this.sentinel = this.querySelector('.infinite-scroll__sentinel');
    this.endMessage = this.querySelector('.infinite-scroll__end');

    this.log('productGrid:', this.productGrid);
    this.log('totalPages:', this.totalPages, 'currentPage:', this.currentPage);

    this.setupRestoreListener();

    // 如果只有一页或找不到商品网格，则不启用无限滚动
    if (!this.productGrid || this.totalPages <= 1) {
      this.enabled = false;
      this.restoreClickedProductPosition();
      this.log('无限滚动未启用 - productGrid:', !!this.productGrid, 'totalPages:', this.totalPages);
      return;
    }

    // 添加标记类
    document.body.classList.add('infinite-scroll-enabled');

    // 隐藏传统分页
    this.hidePagination();

    // 使用滚动事件监听
    this.setupScrollListener();

    // 监听筛选变化事件
    this.setupFilterListener();
    this.restoreClickedProductPosition();

    this.log('无限滚动已启用');
  }

  disconnectedCallback() {
    if (this.observer) {
      this.observer.disconnect();
    }
    if (this.scrollHandler) {
      window.removeEventListener('scroll', this.scrollHandler);
    }
    if (this.gridObserver) {
      this.gridObserver.disconnect();
    }
    if (this.restoreHandler) {
      window.removeEventListener('pageshow', this.restoreHandler);
    }
    this.restoreCleanup?.();
    document.body.classList.remove('infinite-scroll-enabled');
  }

  setupRestoreListener() {
    if (this.restoreHandler) return;

    this.restoreHandler = (event) => this.restoreClickedProductPosition({ persisted: Boolean(event?.persisted) });
    window.addEventListener('pageshow', this.restoreHandler);
  }

  collectionKeyFromLocation() {
    const url = new URL(window.location.href);
    url.searchParams.delete('page');
    url.searchParams.delete('section_id');

    const search = url.searchParams.toString();
    return `${url.pathname}${search ? `?${search}` : ''}`;
  }

  readRestoreTarget() {
    try {
      const rawTarget = window.sessionStorage.getItem(COLLECTION_RESTORE_KEY);
      const target = rawTarget
        ? JSON.parse(rawTarget)
        : window.history.state?.luredollsCollectionRestore;

      if (!target) return null;

      const isFresh = Date.now() - Number(target.createdAt || 0) < COLLECTION_RESTORE_MAX_AGE;
      if (!target.productId || target.collectionKey !== this.collectionKeyFromLocation() || !isFresh) return null;
      if (Number(target.createdAt || 0) === this.consumedRestoreCreatedAt) return null;

      return target;
    } catch (error) {
      return null;
    }
  }

  clearRestoreTarget(expectedCreatedAt = 0) {
    try {
      const rawTarget = window.sessionStorage.getItem(COLLECTION_RESTORE_KEY);
      const storageTarget = rawTarget ? JSON.parse(rawTarget) : null;
      if (!expectedCreatedAt || Number(storageTarget?.createdAt || 0) === expectedCreatedAt) {
        window.sessionStorage.removeItem(COLLECTION_RESTORE_KEY);
        window.sessionStorage.removeItem(COLLECTION_RESTORE_SNAPSHOT_KEY);
      }
    } catch (error) {}

    try {
      const currentState = window.history.state;
      const historyTarget = currentState && typeof currentState === 'object'
        ? currentState.luredollsCollectionRestore
        : null;
      if (
        historyTarget
        && (!expectedCreatedAt || Number(historyTarget.createdAt || 0) === expectedCreatedAt)
      ) {
        const nextState = { ...currentState };
        delete nextState.luredollsCollectionRestore;
        window.history.replaceState(nextState, document.title, window.location.href);
      }
    } catch (error) {}

    if (
      !expectedCreatedAt
      || Number(window.__luredollsCollectionRestoreTarget?.createdAt || 0) === expectedCreatedAt
    ) {
      window.__luredollsCollectionRestoreTarget = null;
      window.__luredollsCollectionRestoreSnapshot = null;
    }
  }

  deferNativeRestoreCleanup(target) {
    const createdAt = Number(target?.createdAt || 0);
    if (!createdAt) return;

    this.consumedRestoreCreatedAt = createdAt;
    window.__luredollsCollectionRestoreSettlingCreatedAt = createdAt;
    window.clearTimeout(this.restoreClearTimer);

    const clearAfterGestureSettles = () => {
      this.clearRestoreTarget(createdAt);
      if (this.consumedRestoreCreatedAt === createdAt) this.consumedRestoreCreatedAt = 0;
      if (window.__luredollsCollectionRestoreSettlingCreatedAt === createdAt) {
        window.__luredollsCollectionRestoreSettlingCreatedAt = 0;
      }
      if ('scrollRestoration' in window.history) {
        window.history.scrollRestoration = 'auto';
      }
    };

    this.restoreClearTimer = window.setTimeout(clearAfterGestureSettles, COLLECTION_NATIVE_RESTORE_SETTLE_DELAY);
    window.addEventListener('pagehide', clearAfterGestureSettles, { once: true });
  }

  findProductCard(productId) {
    return Array.from(document.querySelectorAll('[data-product-click-item]')).find((card) => card.dataset.productId === productId);
  }

  readRestoreSnapshot(target) {
    try {
      const rawSnapshot = window.sessionStorage.getItem(COLLECTION_RESTORE_SNAPSHOT_KEY);
      if (!rawSnapshot) return null;

      const snapshot = JSON.parse(rawSnapshot);
      const isFresh = Date.now() - Number(snapshot.createdAt || 0) < COLLECTION_RESTORE_MAX_AGE;
      if (
        snapshot.version !== COLLECTION_RESTORE_SNAPSHOT_VERSION ||
        !snapshot.gridHtml ||
        snapshot.collectionKey !== target.collectionKey ||
        !isFresh
      ) {
        return null;
      }

      return snapshot;
    } catch (error) {
      return null;
    }
  }

  restoreCachedGridSnapshot(target) {
    const snapshot = this.readRestoreSnapshot(target);
    if (!snapshot || !this.productGrid) return null;

    this.productGrid.innerHTML = snapshot.gridHtml;
    this.productGrid.dataset.collectionRestoreSnapshot = 'true';
    this.currentPage = Math.max(Number(snapshot.currentPage || target.currentPage || this.currentPage || 1), 1);
    this.totalPages = Math.max(Number(snapshot.totalPages || target.totalPages || this.totalPages || 1), this.currentPage);
    this.dataset.currentPage = this.currentPage;
    this.dataset.totalPages = this.totalPages;

    return this.findProductCard(target.productId);
  }

  prepareRestoreViewport(target, scrollNow = true) {
    const scrollY = Math.max(Number(target.scrollY || 0), 0);

    const restoreHeight = Math.max(
      scrollY + window.innerHeight,
      document.documentElement.clientHeight,
      Number(target.documentHeight || 0),
      document.body ? document.body.scrollHeight : 0
    );

    document.documentElement.classList.add('collection-restore-pending');
    if (target.hasSnapshot) {
      document.documentElement.classList.add('collection-restore-has-snapshot');
    }
    document.documentElement.style.setProperty('--collection-restore-min-height', `${restoreHeight}px`);
    document.documentElement.style.minHeight = `${restoreHeight}px`;

    if (scrollNow && scrollY) {
      window.scrollTo(0, scrollY);
    }
  }

  finishRestoreViewport({ restoreScrollRestoration = true } = {}) {
    window.__luredollsCollectionRestoreCompleted = true;
    document.documentElement.classList.remove('collection-restore-pending');
    document.documentElement.classList.remove('collection-restore-has-snapshot');
    document.documentElement.style.removeProperty('--collection-restore-min-height');
    document.documentElement.style.removeProperty('min-height');

    if (restoreScrollRestoration && 'scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'auto';
    }
  }

  async loadPagesThrough(targetPage) {
    const finalPage = Math.min(Math.max(Number(targetPage || this.currentPage), this.currentPage), this.totalPages);
    if (finalPage <= this.currentPage) return;

    const pages = [];
    for (let page = this.currentPage + 1; page <= finalPage; page += 1) {
      pages.push(page);
    }

    this.loading = true;
    this.showLoading();

    try {
      const pageResults = await Promise.all(
        pages.map((page) => this.fetchPageProducts(page).catch((error) => {
          console.error('Infinite scroll: Failed to load restore page:', error);
          return { page, products: [] };
        }))
      );

      pageResults
        .sort((a, b) => a.page - b.page)
        .some(({ page, products }) => {
          if (page !== this.currentPage + 1 || !products.length) return true;

          this.appendProducts(products, page);
          return false;
        });

      this.updateCompletedState();
    } finally {
      this.loading = false;
      this.hideLoading();
    }
  }

  async loadUntilProductIsRestored(productId, target = {}) {
    const targetPage = Number(target.targetPage || target.currentPage || 0);
    if (!this.findProductCard(productId) && targetPage > this.currentPage) {
      await this.loadPagesThrough(targetPage);
    }

    while (!this.findProductCard(productId) && this.currentPage < this.totalPages) {
      const pageBeforeLoad = this.currentPage;
      await this.loadNextPage();

      if (this.currentPage === pageBeforeLoad) break;
    }

    return this.findProductCard(productId);
  }

  stabilizeProductCardPosition(card, target, { persisted = false } = {}) {
    this.restoreCleanup?.();

    const savedViewportTop = Number(target.viewportTop);
    const hasSavedViewportTop = Number.isFinite(savedViewportTop);
    const startedAt = performance.now();
    const minimumLockDuration = persisted ? 900 : 1300;
    const maximumLockDuration = persisted ? 2200 : 3000;
    const interruptEvents = ['pointerdown', 'touchstart', 'wheel', 'keydown'];
    let lastCorrectionAt = startedAt;
    let correctionFrame = 0;
    let stopped = false;
    let centeredLegacyTarget = false;
    let resizeObserver = null;

    const removeInterruptListeners = () => {
      interruptEvents.forEach((eventName) => window.removeEventListener(eventName, interruptRestore));
    };

    const cleanup = () => {
      if (stopped) return;
      stopped = true;
      window.cancelAnimationFrame(correctionFrame);
      resizeObserver?.disconnect();
      removeInterruptListeners();
    };

    const complete = () => {
      cleanup();
      this.restoreCleanup = null;
      this.restoreCreatedAt = 0;
      this.finishRestoreViewport();
      this.clearRestoreTarget();
    };

    function interruptRestore() {
      complete();
    }

    const correctCard = () => {
      correctionFrame = 0;
      if (stopped || !card.isConnected) {
        complete();
        return;
      }

      if (hasSavedViewportTop) {
        const delta = card.getBoundingClientRect().top - savedViewportTop;
        if (Math.abs(delta) > 1) {
          window.scrollBy(0, delta);
          lastCorrectionAt = performance.now();
        }
      } else if (!centeredLegacyTarget) {
        card.scrollIntoView({ block: 'center', inline: 'nearest' });
        centeredLegacyTarget = true;
        lastCorrectionAt = performance.now();
      }

      const elapsed = performance.now() - startedAt;
      const quietFor = performance.now() - lastCorrectionAt;
      if (elapsed >= maximumLockDuration || (elapsed >= minimumLockDuration && quietFor >= 500)) {
        complete();
        return;
      }

      correctionFrame = window.requestAnimationFrame(correctCard);
    };

    interruptEvents.forEach((eventName) => {
      window.addEventListener(eventName, interruptRestore, { once: true, passive: true });
    });

    if ('ResizeObserver' in window) {
      resizeObserver = new ResizeObserver(() => {
        if (!correctionFrame && !stopped) correctionFrame = window.requestAnimationFrame(correctCard);
      });
      resizeObserver.observe(document.body);
      if (this.productGrid) resizeObserver.observe(this.productGrid);
    }

    this.restoreCleanup = cleanup;
    this.restoreCreatedAt = target.createdAt;
    correctionFrame = window.requestAnimationFrame(correctCard);
  }

  alignCachedProductCard(card, target) {
    const savedViewportTop = Number(target.viewportTop);
    if (Number.isFinite(savedViewportTop)) {
      const delta = card.getBoundingClientRect().top - savedViewportTop;
      if (Math.abs(delta) > 1) window.scrollBy(0, delta);
      return;
    }

    const savedScrollY = Number(target.scrollY);
    if (Number.isFinite(savedScrollY) && savedScrollY > 0) {
      window.scrollTo(0, savedScrollY);
    }
  }

  async restoreClickedProductPosition({ persisted = false } = {}) {
    if (this.restoringProductPosition) return;
    if (window.__luredollsCollectionRestoreCompleted) return;

    const target = this.readRestoreTarget();
    if (!target) return;
    if (this.restoreCleanup && this.restoreCreatedAt === target.createdAt) return;

    this.restoringProductPosition = true;

    try {
      let card = this.findProductCard(target.productId);

      // A BFCache collection already contains every loaded card. Its history
      // entry uses manual restoration after pagehide, so align the saved card
      // once during pageshow before the committed page is painted. Do not start
      // the multi-frame lock or replace the grid during the swipe gesture.
      if (persisted && card) {
        this.alignCachedProductCard(card, target);
        this.finishRestoreViewport({ restoreScrollRestoration: false });
        this.deferNativeRestoreCleanup(target);
        return;
      }

      card = card || this.restoreCachedGridSnapshot(target);

      // Cold returns stay on rendered content while missing infinite-scroll
      // pages are rebuilt. Never jump into the reserved empty height first.
      this.prepareRestoreViewport(target, false);
      document.documentElement.classList.remove('collection-restore-has-snapshot');

      if (!card && window.scrollY) window.scrollTo(0, 0);

      card = card || await this.loadUntilProductIsRestored(target.productId, target);

      if (card) {
        this.stabilizeProductCardPosition(card, target, { persisted });
      } else if (target.scrollY) {
        window.scrollTo(0, 0);
        this.finishRestoreViewport();
        this.clearRestoreTarget();
      } else {
        this.finishRestoreViewport();
        this.clearRestoreTarget();
      }
    } finally {
      this.restoringProductPosition = false;
    }
  }

  /**
   * 设置滚动事件监听
   * 以 infinite-scroll 组件（原分页按钮位置）为基准，而不是页面底部
   */
  setupScrollListener() {
    let lastCall = 0;
    const delay = 100;
    const self = this;

    this.scrollHandler = function() {
      const now = Date.now();
      if (now - lastCall < delay) return;
      lastCall = now;

      if (self.loading || !self.enabled || self.currentPage >= self.totalPages) return;

      // 获取 infinite-scroll 组件的位置（它在原分页按钮的位置）
      const componentRect = self.getBoundingClientRect();
      const windowHeight = window.innerHeight;

      // 计算组件顶部距离视口底部的距离
      const distanceToViewport = componentRect.top - windowHeight;

      // 当组件距离视口底部小于 threshold 时触发加载
      if (distanceToViewport <= self.threshold) {
        self.log('触发加载 - 距视口:', Math.round(distanceToViewport), 'px');
        self.loadNextPage();
      }
    };

    window.addEventListener('scroll', this.scrollHandler, { passive: true });
    this.log('滚动监听已设置，threshold:', this.threshold);

    // 立即检查一次，防止页面初始就在底部
    setTimeout(() => {
      this.scrollHandler();
    }, 500);
  }

  /**
   * 监听筛选变化，当筛选条件改变时重置无限滚动
   */
  setupFilterListener() {
    // 监听 popstate 事件（浏览器前进/后退）
    window.addEventListener('popstate', this.handleFilterChange.bind(this));

    // 创建 MutationObserver 监听 ProductGridContainer 的变化
    const productGridContainer = document.getElementById('ProductGridContainer');
    if (productGridContainer) {
      this.gridObserver = new MutationObserver((mutations) => {
        // 当网格内容被替换时（通常是筛选操作），重置无限滚动
        mutations.forEach((mutation) => {
          if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
            this.resetAfterFilter();
          }
        });
      });

      this.gridObserver.observe(productGridContainer, {
        childList: true,
        subtree: false
      });
    }
  }

  /**
   * 处理筛选变化
   */
  handleFilterChange() {
    // A cross-document product return also emits popstate in Safari. Let the
    // pageshow restore transaction own it instead of resetting the grid midway.
    if (this.readRestoreTarget() || window.__luredollsCollectionRestoreSettlingCreatedAt) return;

    // 延迟执行以确保DOM已更新
    setTimeout(() => {
      this.resetAfterFilter();
    }, 100);
  }

  /**
   * 筛选后重置无限滚动状态
   */
  resetAfterFilter() {
    // 重新查找商品网格（可能已被替换）
    this.productGrid = document.getElementById('product-grid');

    // 从URL获取当前页码
    const urlParams = new URLSearchParams(window.location.search);
    this.currentPage = parseInt(urlParams.get('page')) || 1;

    // 尝试从新的分页元素获取总页数
    this.updateTotalPages();

    // 重置状态
    this.loading = false;
    this.enabled = this.totalPages > 1;

    // 重置UI
    if (this.endMessage) {
      this.endMessage.classList.add('hidden');
    }
    if (this.loadingIndicator) {
      this.loadingIndicator.classList.add('hidden');
    }

    // 隐藏分页
    this.hidePagination();

    // 重新设置观察器
    if (this.enabled) {
      this.setupObserver();
    }
  }

  /**
   * 更新总页数（从分页元素中获取）
   */
  updateTotalPages() {
    // 尝试从分页链接获取总页数
    const paginationItems = document.querySelectorAll('.pagination__item:not(.pagination__item-arrow)');
    if (paginationItems.length > 0) {
      const lastPageItem = paginationItems[paginationItems.length - 1];
      const lastPageNum = parseInt(lastPageItem.textContent);
      if (!isNaN(lastPageNum)) {
        this.totalPages = lastPageNum;
      }
    }

    // 更新 data 属性
    this.dataset.totalPages = this.totalPages;
    this.dataset.currentPage = this.currentPage;
  }

  /**
   * 隐藏传统分页组件
   */
  hidePagination() {
    const pagination = document.querySelector('.pagination-wrapper');
    if (pagination) {
      pagination.style.display = 'none';
    }
  }

  /**
   * 加载下一页商品
   */
  async loadNextPage() {
    if (this.loading || this.currentPage >= this.totalPages) {
      this.log('跳过加载 - loading:', this.loading, 'currentPage:', this.currentPage, 'totalPages:', this.totalPages);
      return;
    }

    this.loading = true;
    this.showLoading();

    const nextPage = this.currentPage + 1;
    this.log('正在加载第', nextPage, '页，URL:', this.buildUrl(nextPage));

    try {
      const { products } = await this.fetchPageProducts(nextPage);
      this.appendProducts(products, nextPage);
      this.updateCompletedState();
    } catch (error) {
      console.error('Infinite scroll: Failed to load next page:', error);
      this.log('加载失败:', error.message);

      // 显示错误提示并允许重试
      this.showError();
    } finally {
      this.loading = false;
      this.hideLoading();
    }
  }

  async fetchPageProducts(page) {
    const url = this.buildUrl(page);
    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const html = await response.text();
    this.log('获取到 HTML 响应，长度:', html.length);

    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const newProductGrid = doc.getElementById('product-grid');
    const products = newProductGrid ? Array.from(newProductGrid.querySelectorAll('.grid__item')) : [];

    this.log('找到新商品数量:', products.length);

    return { page, products };
  }

  appendProducts(products, page) {
    if (!products.length || !this.productGrid) return;

    const fragment = document.createDocumentFragment();

    products.forEach((product) => {
      const clonedProduct = product.cloneNode(true);
      clonedProduct.dataset.collectionPage = String(page);

      // 移除滚动动画触发器以避免重复动画问题
      clonedProduct.querySelectorAll('.scroll-trigger').forEach((el) => {
        el.classList.add('scroll-trigger--cancel');
      });

      fragment.appendChild(clonedProduct);
    });

    this.productGrid.appendChild(fragment);

    this.currentPage = page;
    this.dataset.currentPage = String(page);
    this.log('已加载第', page, '页，共', products.length, '个商品');

    // 触发自定义事件，供其他组件监听
    this.dispatchEvent(new CustomEvent('infinite-scroll:page-loaded', {
      bubbles: true,
      detail: {
        page,
        totalPages: this.totalPages,
        newProductsCount: products.length
      }
    }));

    // 初始化新加载商品的动画（如果有的话）
    if (typeof initializeScrollAnimationTrigger === 'function') {
      initializeScrollAnimationTrigger();
    }
  }

  updateCompletedState() {
    if (this.currentPage < this.totalPages) return;

    this.log('已加载全部页面');
    this.showEndMessage();
    if (this.observer) {
      this.observer.disconnect();
    }
    if (this.scrollHandler) {
      window.removeEventListener('scroll', this.scrollHandler);
    }
  }

  /**
   * 构建请求URL
   * @param {number} page - 页码
   * @returns {string} - 完整URL
   */
  buildUrl(page) {
    const url = new URL(window.location.href);
    url.searchParams.set('page', page);

    // 添加 section_id 以获取部分渲染（Shopify Section Rendering API）
    const sectionId = this.productGrid?.dataset.id;
    if (sectionId) {
      url.searchParams.set('section_id', sectionId);
    }

    return url.toString();
  }

  /**
   * 显示加载指示器
   */
  showLoading() {
    if (this.loadingIndicator) {
      this.loadingIndicator.classList.remove('hidden');
    }
  }

  /**
   * 隐藏加载指示器
   */
  hideLoading() {
    if (this.loadingIndicator) {
      this.loadingIndicator.classList.add('hidden');
    }
  }

  /**
   * 显示结束提示
   */
  showEndMessage() {
    if (this.endMessage) {
      this.endMessage.classList.remove('hidden');
    }
  }

  /**
   * 显示错误提示
   */
  showError() {
    // 创建错误提示元素（如果不存在）
    let errorElement = this.querySelector('.infinite-scroll__error');
    if (!errorElement) {
      errorElement = document.createElement('div');
      errorElement.className = 'infinite-scroll__error';
      errorElement.innerHTML = `
        <p>加载失败，请重试</p>
        <button class="infinite-scroll__retry button button--secondary" type="button">重试</button>
      `;
      this.insertBefore(errorElement, this.sentinel);

      // 绑定重试按钮事件
      const retryButton = errorElement.querySelector('.infinite-scroll__retry');
      if (retryButton) {
        retryButton.addEventListener('click', () => {
          errorElement.remove();
          this.loadNextPage();
        });
      }
    }
    errorElement.classList.remove('hidden');
  }

  /**
   * 重置无限滚动状态（供外部调用）
   * @param {number} totalPages - 新的总页数
   */
  reset(totalPages = null) {
    this.log('重置无限滚动，新总页数:', totalPages);
    this.currentPage = 1;
    if (totalPages !== null) {
      this.totalPages = totalPages;
    }
    this.loading = false;
    this.enabled = this.totalPages > 1;

    // 重新获取商品网格引用
    this.productGrid = document.getElementById('product-grid');

    // 重置UI状态
    if (this.endMessage) {
      this.endMessage.classList.add('hidden');
    }

    const errorElement = this.querySelector('.infinite-scroll__error');
    if (errorElement) {
      errorElement.remove();
    }

    // 隐藏分页
    this.hidePagination();

    // 重新设置观察器和滚动监听
    if (this.enabled) {
      this.setupObserver();
      // 移除旧的滚动监听
      if (this.scrollHandler) {
        window.removeEventListener('scroll', this.scrollHandler);
      }
      this.setupScrollListener();
    }
  }
}

// 注册自定义元素
customElements.define('infinite-scroll', InfiniteScroll);

// 导出用于与 FacetFiltersForm 集成的重置函数
window.resetInfiniteScroll = function(totalPages = null) {
  const infiniteScroll = document.querySelector('infinite-scroll');
  if (infiniteScroll) {
    infiniteScroll.reset(totalPages);
  }
};
